import React, { useState } from 'react'
import { Box, Chip, Typography, Tooltip } from '@mui/material'
import {
  AutoCoderFramework,
  AutoCoderFrameworkMatch,
  AutoCoderRecordData,
  AutoCoderVariable,
} from '../../types/gotypes'
import { getDeterministicColor } from '../social-media/design-tokens'

interface MatchEvidenceProps {
  record?: AutoCoderRecordData
  /**
   * The matches to highlight, overriding `record.matches`.
   *
   * Since the detach a record has ONE source text and many codings, so the
   * text still comes from the record while the answer comes from a single
   * coding's `result`. Callers stacking codings pass this; legacy callers pass
   * only `record` and keep the old behaviour.
   */
  matches?: AutoCoderFrameworkMatch[]
  frameworks?: AutoCoderFramework[]
  colorMap?: Record<string, string>
  showLoopedVariables?: boolean
  showTooltips?: boolean
  /** The source deleted the text: show the codes alone instead of "No source text available." */
  textRemoved?: boolean
}

interface NormalizedLoopMatch {
  name: string
  code: string
  label: string
  explanation: string
}

interface NormalizedFrameworkMatch {
  id: string
  offset: number
  end: number
  color: string
  rootLabel: string
  rootCode: string
  rootName: string
  rootExplanation: string
  looped: NormalizedLoopMatch[]
}

interface TextSegment {
  id: string
  text: string
  matchId: string | null
  // Track all matches that overlap this segment for priority handling
  allMatchIds: string[]
}

const buildLabelMap = (frameworks: AutoCoderFramework[]) => {
  const map: Record<string, Record<string, string>> = {}

  const addVariable = (variable: AutoCoderVariable) => {
    if (!variable || !variable.name) return
    if (!map[variable.name]) {
      map[variable.name] = {}
    }
    variable.codes.forEach(code => {
      if (!code.code) return
      const label = code.text || code.code
      map[variable.name][code.code] = label
    })
  }

  frameworks.forEach(framework => {
    addVariable(framework.main_variable)
    framework.looped_variables?.forEach(addVariable)
  })

  return map
}

const findLabel = (
  labelMap: Record<string, Record<string, string>>,
  variableName: string,
  code: string,
) => {
  if (!variableName || !code) return code
  const variableEntry = labelMap[variableName]
  if (!variableEntry) return code
  return variableEntry[code] || code
}

/**
 * Find the offset and length of matching text within source text
 * Returns {offset: 0, length: 0} if not found
 */
const findTextOffset = (
  sourceText: string,
  matchingText: string,
): { offset: number; length: number } => {
  if (!sourceText || !matchingText) {
    return { offset: 0, length: 0 }
  }

  const trimmedMatchingText = matchingText.trim()
  if (!trimmedMatchingText) {
    return { offset: 0, length: 0 }
  }

  // Try case-insensitive search first
  const lowerSource = sourceText.toLowerCase()
  const lowerMatch = trimmedMatchingText.toLowerCase()
  let offset = lowerSource.indexOf(lowerMatch)

  if (offset === -1) {
    // Fallback to case-sensitive search
    offset = sourceText.indexOf(trimmedMatchingText)
  }

  if (offset === -1) {
    return { offset: 0, length: 0 }
  }

  return {
    offset,
    length: trimmedMatchingText.length,
  }
}

const normalizeMatches = (
  sourceText: string,
  sourceMatches: AutoCoderFrameworkMatch[] | undefined,
  frameworks: AutoCoderFramework[] | undefined,
  colorMap: Record<string, string> | undefined,
) => {
  if (!sourceMatches || sourceMatches.length === 0) {
    return [] as NormalizedFrameworkMatch[]
  }

  const labelMap = buildLabelMap(frameworks || [])
  const normalized: NormalizedFrameworkMatch[] = []
  const assignedColors: Record<string, string> = {}

  sourceMatches.forEach((match, index) => {
    const rootCode = match.root_match?.code || ''
    if (!rootCode) return

    const trimmedCode = rootCode.trim()
    if (!trimmedCode) return

    const rootName = match.root_match?.name || ''
    const label = findLabel(labelMap, rootName, trimmedCode)
    // Use lowercase key for colorMap lookup to match chart generators
    const colorKey = label.toLowerCase()
    const hasProvidedColor = Boolean(colorMap && colorMap[colorKey])
    const existingColor = assignedColors[colorKey]
    let color = existingColor

    if (hasProvidedColor) {
      color = (colorMap as Record<string, string>)[colorKey]
      assignedColors[colorKey] = color
    } else if (!color) {
      // Use deterministic color for unknown codes - ensures consistency with charts
      color = getDeterministicColor(colorKey)
      assignedColors[colorKey] = color
    }

    // Calculate offset and length from matching_text
    const matchingText = match.matching_text || ''
    const { offset: baseOffset, length: baseLength } = findTextOffset(sourceText, matchingText)

    const looped = (match.looped_matches || []).map(loopedMatch => {
      const loopCode = loopedMatch.code || ''
      const loopName = loopedMatch.name || ''
      return {
        name: loopName,
        code: loopCode,
        label: findLabel(labelMap, loopName, loopCode),
        explanation: loopedMatch.explanation || '',
      }
    })

    normalized.push({
      id: `${rootName || 'match'}-${trimmedCode}-${index}`,
      offset: baseOffset,
      end: baseOffset + baseLength,
      color,
      rootLabel: label,
      rootCode: trimmedCode,
      rootName: rootName || 'main',
      rootExplanation: match.root_match?.explanation || '',
      looped,
    })
  })

  return normalized
}

const buildTextSegments = (
  text: string,
  matches: NormalizedFrameworkMatch[],
) => {
  if (!text) {
    return [{ id: 'empty', text: '', matchId: null, allMatchIds: [] }] as TextSegment[]
  }

  const segments: TextSegment[] = []
  if (matches.length === 0) {
    segments.push({ id: 'all', text, matchId: null, allMatchIds: [] })
    return segments
  }

  // Check if all matches have invalid pointers (all zeros)
  const hasValidPointers = matches.some(match => match.offset > 0 || match.end > 0)
  if (!hasValidPointers) {
    // If no valid pointers, just show the text without highlighting
    console.warn('MatchEvidence: All pointers are zero - highlighting disabled')
    segments.push({ id: 'all-no-pointers', text, matchId: null, allMatchIds: [] })
    return segments
  }

  // Create boundary points for all match starts and ends
  const boundaries = new Set<number>()
  boundaries.add(0)
  boundaries.add(text.length)
  
  matches.forEach(match => {
    const start = clamp(match.offset, 0, text.length)
    const end = clamp(match.end, 0, text.length)
    boundaries.add(start)
    boundaries.add(end)
  })

  // Sort boundaries to create segments
  const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b)
  
  // Build segments between each pair of boundaries
  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const segmentStart = sortedBoundaries[i]
    const segmentEnd = sortedBoundaries[i + 1]
    
    if (segmentStart >= segmentEnd) continue
    
    // Find all matches that overlap this segment
    const overlappingMatches = matches.filter(match => {
      const matchStart = clamp(match.offset, 0, text.length)
      const matchEnd = clamp(match.end, 0, text.length)
      return matchStart < segmentEnd && matchEnd > segmentStart
    })
    
    const segmentText = text.slice(segmentStart, segmentEnd)
    if (segmentText) {
      // Use the first match as primary, but track all overlapping matches
      const primaryMatch = overlappingMatches[0]
      segments.push({
        id: `segment-${segmentStart}-${segmentEnd}`,
        text: segmentText,
        matchId: primaryMatch ? primaryMatch.id : null,
        allMatchIds: overlappingMatches.map(m => m.id),
      })
    }
  }

  if (segments.length === 0) {
    segments.push({ id: 'all-fallback', text, matchId: null, allMatchIds: [] })
  }

  return segments
}

const clamp = (value: number, min: number, max: number) => {
  if (Number.isNaN(value)) return min
  if (value < min) return min
  if (value > max) return max
  return value
}

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) {
    return `rgba(100, 116, 139, ${alpha})`
  }
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  if ([r, g, b].some(component => Number.isNaN(component))) {
    return `rgba(100, 116, 139, ${alpha})`
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getReadableTextColor = (hex: string) => {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) return '#0F172A'
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  if ([r, g, b].some(component => Number.isNaN(component))) {
    return '#0F172A'
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#0F172A' : '#F8FAFC'
}

const formatLoopLabel = (loop: NormalizedLoopMatch) => {
  if (!loop.name) return loop.label
  return `${loop.name}: ${loop.label}`
}

const formatMainLabel = (match: NormalizedFrameworkMatch) => {
  if (!match.rootName || match.rootName === 'main') return match.rootLabel
  return `${match.rootName}: ${match.rootLabel}`
}

const MatchEvidence: React.FC<MatchEvidenceProps> = ({
  record,
  matches: matchesOverride,
  frameworks = [],
  colorMap,
  showLoopedVariables = false,
  showTooltips = true,
  textRemoved = false,
}) => {
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null)
  const [hoveredMatchId, setHoveredMatchId] = useState<string | null>(null)

  const sourceText = record?.source_text || ''
  const sourceMatches = matchesOverride ?? record?.matches
  const matches = normalizeMatches(sourceText, sourceMatches, frameworks, colorMap)
  const matchMap = (() => {
    const map: Record<string, NormalizedFrameworkMatch> = {}
    matches.forEach(item => {
      map[item.id] = item
    })
    return map
  })()

  const segments = buildTextSegments(sourceText, matches)
  const activeMatchId = hoveredMatchId || selectedMatchId

  if (!sourceText && !textRemoved) {
    return (
      <Typography variant='body2' color='text.secondary'>No source text available.</Typography>
    )
  }

  const handleSelect = (id: string) => {
    setSelectedMatchId(prev => (prev === id ? null : id))
  }

  const chips = matches.length === 0 ? null : (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 0.75,
      }}
    >
      {matches.map(match => {
        const isActive = activeMatchId === match.id
        const isDimmed = Boolean(activeMatchId && activeMatchId !== match.id)
        return (
          <Box key={match.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Tooltip
                title={match.rootExplanation || ''}
                arrow
                placement="top"
                disableHoverListener={!showTooltips}
                disableFocusListener={!showTooltips}
                slotProps={{
                  popper: {
                    modifiers: [
                      { name: 'preventOverflow', enabled: false },
                      { name: 'flip', enabled: false },
                    ],
                  },
                }}
              >
              <Chip
                label={formatMainLabel(match)}
                size='small'
                onMouseEnter={() => setHoveredMatchId(match.id)}
                onMouseLeave={() => setHoveredMatchId(prev => (prev === match.id ? null : prev))}
                onClick={() => handleSelect(match.id)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: match.color,
                  color: getReadableTextColor(match.color),
                  border: isActive ? '2px solid rgba(15, 23, 42, 0.2)' : '1px solid transparent',
                  opacity: isDimmed ? 0.6 : 1,
                }}
              />
            </Tooltip>
            {showLoopedVariables && match.looped.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {match.looped.map(loop => (
                  <Tooltip
                      key={`${match.id}-${loop.name}-${loop.code}`}
                      title={loop.explanation || ''}
                      arrow
                      placement="top"
                      disableHoverListener={!showTooltips}
                      disableFocusListener={!showTooltips}
                      slotProps={{
                        popper: {
                          modifiers: [
                            { name: 'preventOverflow', enabled: false },
                            { name: 'flip', enabled: false },
                          ],
                        },
                      }}
                    >
                    <Chip
                      label={formatLoopLabel(loop)}
                      size='small'
                      variant='outlined'
                      sx={{
                        borderColor: hexToRgba(match.color, 0.6),
                        color: match.color,
                        backgroundColor: isActive ? hexToRgba(match.color, 0.1) : 'transparent',
                        // Make looped matches smaller than main matches
                        fontSize: '0.70rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          fontSize: '0.70rem',
                          paddingX: '6px',
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            ) : null}
          </Box>
        )
      })}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      {/* Show text and chips on the same line to maximize vertical space */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box
          sx={{
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            flex: 1,
            minWidth: 0, // Allow text to shrink when needed
          }}
        >
          {segments.map(segment => {
            if (!segment.text) return null
            if (!segment.matchId) {
              return (
                <Box key={segment.id} component='span'>
                  {segment.text}
                </Box>
              )
            }
            
            // Determine which match to use for styling based on active state
            let effectiveMatch = matchMap[segment.matchId]
            let effectiveMatchId = segment.matchId
            
            // If there's an active match and this segment contains it, prioritize it
            if (activeMatchId && segment.allMatchIds.includes(activeMatchId)) {
              effectiveMatch = matchMap[activeMatchId]
              effectiveMatchId = activeMatchId
            }
            
            if (!effectiveMatch) {
              return (
                <Box key={segment.id} component='span'>
                  {segment.text}
                </Box>
              )
            }
            
            const isActiveSegment = activeMatchId === effectiveMatchId
            const hasMultipleMatches = segment.allMatchIds.length > 1
            
            // Use stronger highlighting for active matches when there are overlaps
            const backgroundColor = hexToRgba(
              effectiveMatch.color, 
              isActiveSegment ? (hasMultipleMatches ? 0.45 : 0.35) : 0.18
            )
            const outlineColor = hexToRgba(
              effectiveMatch.color, 
              isActiveSegment ? (hasMultipleMatches ? 0.8 : 0.6) : 0.3
            )
            
            return (
              <Box
                key={segment.id}
                component='span'
                sx={{
                  backgroundColor,
                  color: '#0F172A',
                  borderBottom: `2px solid ${outlineColor}`,
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  // Add subtle outline for overlapping segments
                  outline: hasMultipleMatches && isActiveSegment ? `1px solid ${effectiveMatch.color}` : 'none',
                  position: 'relative',
                  zIndex: isActiveSegment ? 2 : 1,
                }}
              >
                {segment.text}
              </Box>
            )
          })}
        </Box>
        {chips && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {chips}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default MatchEvidence
